import React, { useMemo, useState } from "react";

const CHART = {
  width: 800,
  height: 260,
  padding: { top: 24, right: 24, bottom: 48, left: 48 },
};

const buildPath = (points, maxCount, closeArea = false) => {
  if (points.length === 0) return "";

  const innerWidth = CHART.width - CHART.padding.left - CHART.padding.right;
  const innerHeight = CHART.height - CHART.padding.top - CHART.padding.bottom;
  const stepX = points.length > 1 ? innerWidth / (points.length - 1) : 0;

  const coords = points.map((point, index) => {
    const x = CHART.padding.left + stepX * index;
    const y =
      CHART.padding.top +
      innerHeight -
      (maxCount > 0 ? (point.count / maxCount) * innerHeight : 0);
    return { x, y };
  });

  let path = coords.map((coord, index) => {
    const command = index === 0 ? "M" : "L";
    return `${command}${coord.x},${coord.y}`;
  }).join(" ");

  if (closeArea && coords.length > 0) {
    const baseY = CHART.padding.top + innerHeight;
    const last = coords[coords.length - 1];
    const first = coords[0];
    path += ` L${last.x},${baseY} L${first.x},${baseY} Z`;
  }

  return path;
};

export default function ProductivityTrendChart({ trend, loading }) {
  const [view, setView] = useState("monthly");
  const currentSeries = trend?.[view];
  const points = currentSeries?.points || [];
  const total = currentSeries?.total || 0;
  const year = currentSeries?.year || new Date().getFullYear();

  const maxCount = useMemo(
    () => Math.max(...points.map((point) => point.count), 1),
    [points]
  );

  const areaPath = useMemo(
    () => buildPath(points, maxCount, true),
    [points, maxCount]
  );
  const linePath = useMemo(
    () => buildPath(points, maxCount, false),
    [points, maxCount]
  );

  const yTicks = useMemo(() => {
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, index) =>
      Math.round((maxCount / steps) * index)
    );
  }, [maxCount]);

  const innerWidth = CHART.width - CHART.padding.left - CHART.padding.right;
  const innerHeight = CHART.height - CHART.padding.top - CHART.padding.bottom;
  const stepX = points.length > 1 ? innerWidth / (points.length - 1) : 0;

  return (
    <section className="dashboard-trend-section">
      <header className="dashboard-trend-header">
        <div>
          <h2 className="dashboard-trend-title">
            Productividad y flujo de trabajo
          </h2>
          <p className="dashboard-trend-subtitle">
            Cantidad total de pruebas aplicadas en {year}. Actualmente llevas{" "}
            <strong>{total}</strong> prueba{total === 1 ? "" : "s"} registradas.
          </p>
        </div>
        <div className="dashboard-trend-toggle" role="tablist" aria-label="Vista de tendencia">
          <button
            type="button"
            role="tab"
            aria-selected={view === "monthly"}
            className={`dashboard-trend-toggle-btn ${
              view === "monthly" ? "dashboard-trend-toggle-btn--active" : ""
            }`}
            onClick={() => setView("monthly")}
          >
            Por mes
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "weekly"}
            className={`dashboard-trend-toggle-btn ${
              view === "weekly" ? "dashboard-trend-toggle-btn--active" : ""
            }`}
            onClick={() => setView("weekly")}
          >
            Por semana
          </button>
        </div>
      </header>

      {loading ? (
        <p className="dashboard-trend-empty">Cargando tendencia de pruebas...</p>
      ) : total === 0 ? (
        <p className="dashboard-trend-empty">
          Aún no hay pruebas registradas en {year} para mostrar la tendencia.
        </p>
      ) : (
        <div className="dashboard-trend-chart-wrap">
          <svg
            viewBox={`0 0 ${CHART.width} ${CHART.height}`}
            className="dashboard-trend-chart"
            role="img"
            aria-label={`Tendencia de pruebas aplicadas por ${view === "monthly" ? "mes" : "semana"}`}
          >
            {yTicks.map((tick) => {
              const y =
                CHART.padding.top +
                innerHeight -
                (maxCount > 0 ? (tick / maxCount) * innerHeight : 0);
              return (
                <g key={tick}>
                  <line
                    x1={CHART.padding.left}
                    y1={y}
                    x2={CHART.width - CHART.padding.right}
                    y2={y}
                    className="dashboard-trend-grid-line"
                  />
                  <text
                    x={CHART.padding.left - 10}
                    y={y + 4}
                    className="dashboard-trend-axis-label"
                    textAnchor="end"
                  >
                    {tick}
                  </text>
                </g>
              );
            })}

            <path d={areaPath} className="dashboard-trend-area" />
            <path d={linePath} className="dashboard-trend-line" />

            {points.map((point, index) => {
              const x = CHART.padding.left + stepX * index;
              const y =
                CHART.padding.top +
                innerHeight -
                (maxCount > 0 ? (point.count / maxCount) * innerHeight : 0);

              return (
                <g key={point.key}>
                  <circle
                    cx={x}
                    cy={y}
                    r="4"
                    className="dashboard-trend-point"
                  />
                  <title>{`${point.label}: ${point.count} prueba${point.count === 1 ? "" : "s"}`}</title>
                  {(view === "monthly" || index % 2 === 0) && (
                    <text
                      x={x}
                      y={CHART.height - 16}
                      className="dashboard-trend-axis-label"
                      textAnchor="middle"
                    >
                      {point.label}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </section>
  );
}
